import React, { useRef, useEffect, useState } from 'react';
import { branding } from '../../constants/branding';

interface TransactionNode {
  id: string;
  address: string;
  amount: number;
  direction: 'in' | 'out';
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  isExchange: boolean;
  isSanctioned: boolean;
  step: number;
  timestamp: string;
  transactionHash?: string;
}

interface TransactionFlowDiagramProps {
  traceData: {
    rootAddress: string;
    traceSteps: any[];
    totalAmount: number;
    riskScore: number;
  };
  onRenderComplete?: () => void;
}

const TransactionFlowDiagram: React.FC<TransactionFlowDiagramProps> = ({ traceData, onRenderComplete }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedNode, setSelectedNode] = useState<TransactionNode | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastPan, setLastPan] = useState({ x: 0, y: 0 });

  // Handle wheel zoom with proper event management
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (svgRef.current && svgRef.current.contains(e.target as Node)) {
        e.preventDefault();
        e.stopPropagation();
        
        // Handle zoom
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setZoom(prev => Math.max(0.1, Math.min(5, prev * delta)));
      }
    };

    const svgElement = svgRef.current;
    if (svgElement) {
      svgElement.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        svgElement.removeEventListener('wheel', handleWheel);
      };
    }
  }, []);

  // Notify parent when rendering is complete
  useEffect(() => {
    if (traceData && onRenderComplete) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        onRenderComplete();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [traceData, onRenderComplete]);

  // Process trace data into nodes and connections
  const processTraceData = (): { nodes: TransactionNode[], connections: any[] } => {
    console.log('Processing trace data:', traceData);
    console.log('Root address:', traceData.rootAddress);
    console.log('Trace steps count:', traceData.traceSteps?.length || 0);
    console.log('First few trace steps:', traceData.traceSteps?.slice(0, 3));
    
    const nodes: TransactionNode[] = [];
    const connections: any[] = [];
    const nodeMap = new Map<string, TransactionNode>();
    
    if (!traceData?.traceSteps) return { nodes, connections };

    console.log('Processing trace data with', traceData.traceSteps.length, 'steps');

    // Add root node
    const rootNode: TransactionNode = {
      id: 'root',
      address: traceData.rootAddress,
      amount: traceData.totalAmount,
      direction: 'in',
      riskLevel: 'LOW',
      isExchange: false,
      isSanctioned: false,
      step: 0,
      timestamp: new Date().toISOString()
    };
    nodeMap.set('root', rootNode);
    nodes.push(rootNode);

    // Process each trace step - create nodes for all unique addresses
    const allAddresses = new Set<string>();
    const toKey = (addr: string) => (addr || '').toLowerCase();
    allAddresses.add(toKey(traceData.rootAddress));

    // First pass: collect all unique addresses
    traceData.traceSteps.forEach((step) => {
      if (step.fromAddress) allAddresses.add(toKey(step.fromAddress));
      if (step.toAddress) allAddresses.add(toKey(step.toAddress));
      if (step.address) allAddresses.add(toKey(step.address));
    });

    console.log('Found', allAddresses.size, 'unique addresses');

    // Second pass: create nodes for all addresses
    allAddresses.forEach((address, index) => {
      if (address === toKey(traceData.rootAddress)) return; // Skip root, already added
      
      const nodeId = `addr-${address}`;
      if (!nodeMap.has(nodeId)) {
        // Find a representative step for this address to get its properties
        const representativeStep = traceData.traceSteps.find(step => 
          toKey(step.fromAddress) === address || 
          toKey(step.toAddress) === address ||
          toKey(step.address) === address
        );

        // Handle missing or zero amounts for nodes
        let nodeAmount = representativeStep?.amount || 0;
        if (nodeAmount === 0 && traceData.totalAmount > 0) {
          // If individual amounts are 0 but we have a total, distribute it across steps
          nodeAmount = traceData.totalAmount / traceData.traceSteps.length;
        } else if (nodeAmount === 0) {
          // Use a more realistic default amount for Bitcoin transactions
          nodeAmount = 0.01; // Use 0.01 BTC as a more realistic default
        }

        const node: TransactionNode = {
          id: nodeId,
          address: address,
          amount: nodeAmount,
          direction: representativeStep?.direction || 'in',
          riskLevel: representativeStep?.riskLevel || (representativeStep?.isHighRisk ? 'HIGH' : 'LOW'),
          isExchange: representativeStep?.isExchange || false,
          isSanctioned: representativeStep?.isSanctioned || false,
          step: representativeStep?.step || index + 1,
          timestamp: representativeStep?.timestamp || new Date().toISOString(),
          transactionHash: representativeStep?.transactionHash
        };
        nodeMap.set(nodeId, node);
        nodes.push(node);
      }
    });

    // Third pass: create connections for all transactions
    console.log('Creating connections from', traceData.traceSteps.length, 'trace steps');
    traceData.traceSteps.forEach((step, index) => {
      const fromAddress = step.fromAddress;
      const toAddress = step.toAddress;
      // Handle missing or zero amounts - use a default amount or calculate from total
      let amount = step.amount || 0;
      console.log(`Step ${index} amount processing: original=${step.amount}, totalAmount=${traceData.totalAmount}, steps=${traceData.traceSteps.length}`);
      
      if (amount === 0 && traceData.totalAmount > 0) {
        // If individual amounts are 0 but we have a total, distribute it across steps
        amount = traceData.totalAmount / traceData.traceSteps.length;
        console.log(`Distributed amount: ${amount} (from total: ${traceData.totalAmount})`);
      } else if (amount === 0) {
        // Use a more realistic default amount for Bitcoin transactions
        // Bitcoin transactions are typically much larger than 0.001
        amount = 0.01; // Use 0.01 BTC as a more realistic default
        console.log(`Using default amount: ${amount}`);
      }
      const direction = step.direction || 'in';

      console.log(`Step ${index}: from=${fromAddress}, to=${toAddress}, amount=${amount} (original: ${step.amount})`);

      // For Bitcoin, we might have empty fromAddress but valid toAddress
      // Create connections based on what we have
      if (toAddress) {
        const toId = toKey(toAddress) === toKey(traceData.rootAddress) ? 'root' : `addr-${toKey(toAddress)}`;
        
        // If we have a fromAddress, create a normal connection
        if (fromAddress) {
          const fromId = toKey(fromAddress) === toKey(traceData.rootAddress) ? 'root' : `addr-${toKey(fromAddress)}`;
          
          console.log(`  -> fromId=${fromId}, toId=${toId}, fromExists=${nodeMap.has(fromId)}, toExists=${nodeMap.has(toId)}`);
          
          // Only create connection if both nodes exist and it's not a self-connection
          if (fromId !== toId && nodeMap.has(fromId) && nodeMap.has(toId)) {
            connections.push({
              from: fromId,
              to: toId,
              amount: amount,
              direction: direction,
              step: step.step || index + 1,
              timestamp: step.timestamp || new Date().toISOString(),
              transactionHash: step.transactionHash
            });
            console.log(`  -> Created connection: ${fromId} -> ${toId}`);
          } else {
            console.log(`  -> Skipped connection: ${fromId} -> ${toId} (self=${fromId === toId}, fromExists=${nodeMap.has(fromId)}, toExists=${nodeMap.has(toId)})`);
          }
        } else {
          // For Bitcoin with empty fromAddress, create unique external nodes for each transaction
          // This represents different external sources sending to the same address
          const externalId = `external-${step.transactionHash || index}`;
          
          // Create unique external node for each transaction
          if (!nodeMap.has(externalId)) {
            // Handle missing or zero amounts for external nodes
            let externalAmount = amount;
            if (externalAmount === 0 && traceData.totalAmount > 0) {
              // If individual amounts are 0 but we have a total, distribute it across steps
              externalAmount = traceData.totalAmount / traceData.traceSteps.length;
            } else if (externalAmount === 0) {
              // Use a more realistic default amount for Bitcoin transactions
              externalAmount = 0.01; // Use 0.01 BTC as a more realistic default
            }

            const externalNode: TransactionNode = {
              id: externalId,
              address: `External ${index + 1}`,
              amount: externalAmount,
              direction: 'out',
              riskLevel: 'LOW',
              isExchange: false,
              isSanctioned: false,
              step: step.step || index + 1,
              timestamp: step.timestamp || new Date().toISOString(),
              transactionHash: step.transactionHash
            };
            nodeMap.set(externalId, externalNode);
            nodes.push(externalNode);
          }
          
          console.log(`  -> Creating external connection: ${externalId} -> ${toId}, toExists=${nodeMap.has(toId)}`);
          
          if (nodeMap.has(toId)) {
            connections.push({
              from: externalId,
              to: toId,
              amount: amount,
              direction: direction,
              step: step.step || index + 1,
              timestamp: step.timestamp || new Date().toISOString(),
              transactionHash: step.transactionHash
            });
            console.log(`  -> Created external connection: ${externalId} -> ${toId}`);
          }
        }
      } else {
        console.log(`  -> No toAddress, skipping step ${index}`);
      }
    });

    console.log('Processed', nodes.length, 'nodes and', connections.length, 'connections');
    return { nodes, connections };
  };

  const { nodes, connections } = processTraceData();

  // Calculate positions for network layout
  const calculatePositions = () => {
    const centerX = 400;
    const centerY = 300;
    const positions: { [key: string]: { x: number; y: number } } = {};

    // Find root node
    const rootNode = nodes.find(n => n.id === 'root');
    if (rootNode) {
      positions[rootNode.id] = { x: centerX, y: centerY };
    }

    // Get all non-root nodes
    const otherNodes = nodes.filter(n => n.id !== 'root');
    
    // Use a more scalable radial layout
    const nodesPerLayer = 12; // More nodes per layer for better distribution
    const baseRadius = 120;
    const radiusIncrement = 100;

    otherNodes.forEach((node, index) => {
      const layer = Math.floor(index / nodesPerLayer) + 1;
      const positionInLayer = index % nodesPerLayer;
      const angle = (positionInLayer * 2 * Math.PI) / nodesPerLayer;
      const radius = baseRadius + (radiusIncrement * layer);
      
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      positions[node.id] = { x, y };
    });

    return positions;
  };

  const positions = calculatePositions();

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'LOW': return '#4CAF50';
      case 'MEDIUM': return '#FF9800';
      case 'HIGH': return '#F44336';
      case 'CRITICAL': return '#9C27B0';
      default: return '#9E9E9E';
    }
  };

  const getNodeSize = (amount: number) => {
    const maxAmount = Math.max(...nodes.map(n => n.amount));
    const minSize = 20;
    const maxSize = 60;
    // Handle case where all amounts are 0 to prevent NaN
    if (maxAmount === 0) {
      return minSize;
    }
    return minSize + (amount / maxAmount) * (maxSize - minSize);
  };

  const formatAddress = (address: string) => {
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(amount);
  };

  // Debug: Log the trace data structure
  console.log('TransactionFlowDiagram - Trace Data:', traceData);
  console.log('TransactionFlowDiagram - Trace Steps:', traceData?.traceSteps);
  console.log('TransactionFlowDiagram - Root Address:', traceData?.rootAddress);
  
  // Debug: Log sample trace steps to see the data structure
  if (traceData?.traceSteps && traceData.traceSteps.length > 0) {
    console.log('TransactionFlowDiagram - Sample Trace Step:', traceData.traceSteps[0]);
    console.log('TransactionFlowDiagram - Has fromAddress:', traceData.traceSteps[0]?.fromAddress);
    console.log('TransactionFlowDiagram - Has toAddress:', traceData.traceSteps[0]?.toAddress);
    console.log('TransactionFlowDiagram - Direction:', traceData.traceSteps[0]?.direction);
  }

  // If no steps, still render a minimal chart with only the root node
  if (!traceData?.traceSteps || traceData.traceSteps.length === 0) {
    const minimalTrace = {
      ...traceData,
      traceSteps: [],
    };
    // Render the regular diagram container; inner rendering will handle empty connections
    traceData = minimalTrace as any;
  }

  return (
    <div 
      className="transaction-flow-diagram"
      style={{
        width: '100%',
        height: '600px',
        border: `1px solid ${branding.borderColor}`,
        borderRadius: 8,
        backgroundColor: '#fafafa',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Controls */}
      <div style={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 8
      }}>
        <div style={{
          display: 'flex',
          gap: 4,
          backgroundColor: 'white',
          padding: 4,
          borderRadius: 6,
          border: `1px solid ${branding.borderColor}`
        }}>
          <button
            onClick={() => setZoom(prev => Math.min(prev + 0.2, 5))}
            style={{
              padding: '4px 8px',
              backgroundColor: branding.primaryColor,
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12
            }}
          >
            +
          </button>
          <button
            onClick={() => setZoom(prev => Math.max(prev - 0.2, 0.1))}
            style={{
              padding: '4px 8px',
              backgroundColor: branding.primaryColor,
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12
            }}
          >
            -
          </button>
          <button
            onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
            style={{
              padding: '4px 8px',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12
            }}
          >
            Reset
          </button>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '4px 8px',
          borderRadius: 4,
          border: `1px solid ${branding.borderColor}`,
          fontSize: 10,
          textAlign: 'center'
        }}>
          Zoom: {Math.round(zoom * 100)}%
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '4px 8px',
          borderRadius: 4,
          border: `1px solid ${branding.borderColor}`,
          fontSize: 10,
          textAlign: 'center'
        }}>
          Drag to pan
        </div>
      </div>

      {/* Legend */}
      <div style={{
        position: 'absolute',
        top: 10,
        left: 10,
        zIndex: 10,
        backgroundColor: 'white',
        padding: 12,
        borderRadius: 6,
        border: `1px solid ${branding.borderColor}`,
        fontSize: 12
      }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Legend</div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#4CAF50', borderRadius: '50%', marginRight: 6 }}></div>
          Low Risk
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#FF9800', borderRadius: '50%', marginRight: 6 }}></div>
          Medium Risk
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#F44336', borderRadius: '50%', marginRight: 6 }}></div>
          High Risk
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#9C27B0', borderRadius: '50%', marginRight: 6 }}></div>
          Critical Risk
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#2196F3', borderRadius: '50%', marginRight: 6 }}></div>
          Exchange
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#000', borderRadius: '50%', marginRight: 6 }}></div>
          Sanctioned
        </div>
      </div>

      {/* SVG Diagram */}
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        style={{
          transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
          transformOrigin: 'center center',
          transition: isDragging ? 'none' : 'transform 0.3s ease',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            setIsDragging(true);
            setDragStart({ x: e.clientX, y: e.clientY });
            setLastPan({ x: pan.x, y: pan.y });
          }
        }}
        onMouseMove={(e) => {
          if (isDragging) {
            const deltaX = e.clientX - dragStart.x;
            const deltaY = e.clientY - dragStart.y;
            setPan({
              x: lastPan.x + deltaX,
              y: lastPan.y + deltaY
            });
          }
        }}
        onMouseUp={() => {
          setIsDragging(false);
        }}
        onMouseLeave={() => {
          setIsDragging(false);
        }}
        onClick={(e) => {
          // Close modal when clicking on empty space (but not when dragging)
          if (e.target === e.currentTarget && !isDragging) {
            setSelectedNode(null);
          }
        }}
      >
        {/* Connection Lines */}
        {connections.map((connection, index) => {
          const fromPos = positions[connection.from];
          const toPos = positions[connection.to];
          if (!fromPos || !toPos) return null;

          // Determine stroke color based on connection type
          let strokeColor;
          if (connection.direction === 'in') {
            strokeColor = '#4CAF50'; // Green for incoming
          } else if (connection.direction === 'out') {
            strokeColor = '#F44336'; // Red for outgoing
          } else {
            strokeColor = '#2196F3'; // Blue for transfers between addresses
          }
          
          const strokeWidth = Math.max(2, Math.min(8, Math.log(connection.amount + 1) * 2));
          const angle = Math.atan2(toPos.y - fromPos.y, toPos.x - fromPos.x);
          const midX = (fromPos.x + toPos.x) / 2;
          const midY = (fromPos.y + toPos.y) / 2;

          return (
            <g key={`connection-${index}`}>
              {/* Main connection line */}
              <line
                x1={fromPos.x}
                y1={fromPos.y}
                x2={toPos.x}
                y2={toPos.y}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                opacity={0.8}
                strokeDasharray={connection.direction === 'in' ? '5,5' : 'none'}
              />
              
              {/* Arrow head */}
              <polygon
                points={`${toPos.x - 8},${toPos.y - 4} ${toPos.x},${toPos.y} ${toPos.x - 8},${toPos.y + 4}`}
                fill={strokeColor}
                transform={`rotate(${angle * 180 / Math.PI} ${toPos.x} ${toPos.y})`}
              />
              
              {/* Connection label background */}
              <rect
                x={midX - 30}
                y={midY - 12}
                width="60"
                height="24"
                fill="white"
                stroke={strokeColor}
                strokeWidth="1"
                rx="4"
                opacity={0.9}
              />
              
              {/* Amount label */}
              <text
                x={midX}
                y={midY + 2}
                textAnchor="middle"
                fill="#333"
                fontSize="9"
                fontWeight="600"
              >
                {formatAmount(connection.amount)}
              </text>
              
              {/* Step indicator - Now clickable */}
              {connection.step && (
                <g>
                  <circle
                    cx={midX + 25}
                    cy={midY - 8}
                    r="6"
                    fill={strokeColor}
                    stroke="white"
                    strokeWidth="1"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Step indicator clicked:', {
                        step: connection.step,
                        connection,
                        transactionHash: connection.transactionHash
                      });
                      // Create a temporary node for the step indicator
                      const stepNode = {
                        id: `step-${connection.step}-${index}`,
                        address: `${connection.from} → ${connection.to}`,
                        amount: connection.amount,
                        direction: connection.direction,
                        riskLevel: 'MEDIUM' as const,
                        isExchange: false,
                        isSanctioned: false,
                        step: connection.step,
                        timestamp: connection.timestamp,
                        transactionHash: connection.transactionHash
                      };
                      setSelectedNode(stepNode);
                    }}
                    onMouseEnter={(e) => {
                      const target = e.currentTarget as SVGElement;
                      target.style.opacity = '0.8';
                      target.style.strokeWidth = '2';
                    }}
                    onMouseLeave={(e) => {
                      const target = e.currentTarget as SVGElement;
                      target.style.opacity = '1';
                      target.style.strokeWidth = '1';
                    }}
                  />
                  <text
                    x={midX + 25}
                    y={midY - 5}
                    textAnchor="middle"
                    fill="white"
                    fontSize="7"
                    fontWeight="600"
                    style={{ pointerEvents: 'none' }}
                  >
                    {connection.step}
                  </text>
                </g>
              )}
              
              {/* Direction indicator */}
              <text
                x={midX}
                y={midY - 20}
                textAnchor="middle"
                fill={strokeColor}
                fontSize="8"
                fontWeight="500"
              >
                {connection.direction === 'in' ? '📥' : '📤'}
              </text>
            </g>
          );
        })}

        {/* Root Node */}
        {nodes.filter(n => n.id === 'root').map((rootNode) => {
          const pos = positions[rootNode.id];
          if (!pos) return null;

          return (
            <g key={rootNode.id}>
              <circle
                cx={pos.x}
                cy={pos.y}
                r="25"
                fill={branding.primaryColor}
                stroke="white"
                strokeWidth="3"
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  const target = e.currentTarget as SVGElement;
                  target.style.opacity = '0.8';
                  target.style.strokeWidth = '4';
                }}
                onMouseLeave={(e) => {
                  const target = e.currentTarget as SVGElement;
                  target.style.opacity = '1';
                  target.style.strokeWidth = '3';
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Clicked root node:', rootNode);
                  setSelectedNode(rootNode);
                }}
              />
              <text
                x={pos.x}
                y={pos.y + 4}
                textAnchor="middle"
                fill="white"
                fontSize="9"
                fontWeight="600"
              >
                ROOT
              </text>
            </g>
          );
        })}

        {/* Transaction Nodes (excluding root) */}
        {nodes.filter(n => n.id !== 'root').map((node) => {
          const nodePos = positions[node.id];
          if (!nodePos) return null;

          const nodeSize = getNodeSize(node.amount);
          const riskColor = getRiskColor(node.riskLevel);
          const fillColor = node.isSanctioned ? '#000' : 
                           node.isExchange ? '#2196F3' : riskColor;

          // Debug logging for all nodes
          console.log('Rendering node:', {
            id: node.id,
            riskLevel: node.riskLevel,
            fillColor,
            isHighRisk: node.riskLevel === 'HIGH' || node.riskLevel === 'CRITICAL',
            isMediumRisk: node.riskLevel === 'MEDIUM'
          });

          const handleNodeClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            console.log('Node clicked:', {
              id: node.id,
              riskLevel: node.riskLevel,
              isHighRisk: node.riskLevel === 'HIGH' || node.riskLevel === 'CRITICAL',
              isMediumRisk: node.riskLevel === 'MEDIUM',
              fillColor,
              node
            });
            setSelectedNode(node);
          };

          const handleMouseEnter = (e: React.MouseEvent<SVGElement>) => {
            const target = e.currentTarget as SVGElement;
            target.style.opacity = '0.8';
            target.style.strokeWidth = '3';
          };

          const handleMouseLeave = (e: React.MouseEvent<SVGElement>) => {
            const target = e.currentTarget as SVGElement;
            target.style.opacity = '1';
            target.style.strokeWidth = '2';
          };

          return (
            <g key={node.id}>
              {/* Node Shape - Always render as circle for consistency */}
              <circle
                cx={nodePos.x}
                cy={nodePos.y}
                r={nodeSize / 2}
                fill={fillColor}
                stroke="white"
                strokeWidth="2"
                style={{ cursor: 'pointer' }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleNodeClick}
              />
              
              {/* Risk Level Indicator */}
              {node.riskLevel === 'HIGH' || node.riskLevel === 'CRITICAL' ? (
                <circle
                  cx={nodePos.x + nodeSize / 2 - 5}
                  cy={nodePos.y - nodeSize / 2 + 5}
                  r="4"
                  fill="#FFD700"
                  stroke="white"
                  strokeWidth="1"
                  style={{ pointerEvents: 'none' }}
                />
              ) : null}

              {/* Exchange Indicator */}
              {node.isExchange ? (
                <text
                  x={nodePos.x}
                  y={nodePos.y - nodeSize / 2 - 8}
                  textAnchor="middle"
                  fill="#2196F3"
                  fontSize="8"
                  fontWeight="600"
                  style={{ pointerEvents: 'none' }}
                >
                  EX
                </text>
              ) : null}

              {/* Sanctions Indicator */}
              {node.isSanctioned ? (
                <text
                  x={nodePos.x}
                  y={nodePos.y + nodeSize / 2 + 12}
                  textAnchor="middle"
                  fill="#F44336"
                  fontSize="8"
                  fontWeight="600"
                  style={{ pointerEvents: 'none' }}
                >
                  SANCTIONS
                </text>
              ) : null}

              {/* Address Label */}
              <text
                x={nodePos.x}
                y={nodePos.y + nodeSize / 2 + 20}
                textAnchor="middle"
                fill="#333"
                fontSize="8"
                fontWeight="500"
                style={{ pointerEvents: 'none' }}
              >
                {formatAddress(node.address)}
              </text>

              {/* Amount Label */}
              <text
                x={nodePos.x}
                y={nodePos.y + nodeSize / 2 + 32}
                textAnchor="middle"
                fill="#666"
                fontSize="7"
                fontWeight="400"
                style={{ pointerEvents: 'none' }}
              >
                {formatAmount(node.amount)}
              </text>
            </g>
          );
        })}

      </svg>

      {/* Node Details Panel */}
      {selectedNode && (
        <div style={{
          position: 'absolute',
          bottom: 10,
          left: 10,
          right: 10,
          backgroundColor: 'white',
          padding: 16,
          borderRadius: 8,
          border: `1px solid ${branding.borderColor}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 10
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ margin: 0, color: branding.primaryColor }}>
              {selectedNode.id === 'root' ? 'Root Address' : 
               selectedNode.id.startsWith('step-') ? `Transaction Step ${selectedNode.step}` :
               `${selectedNode.riskLevel} Risk Address`}
            </h4>
            <button
              onClick={() => setSelectedNode(null)}
              style={{
                padding: '4px 8px',
                backgroundColor: 'transparent',
                border: '1px solid #ccc',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              ✕
            </button>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
            <div>
              <strong>Address:</strong>
              <div style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}>
                {selectedNode.address}
              </div>
            </div>
            <div>
              <strong>Amount:</strong>
              <div style={{ color: selectedNode.direction === 'in' ? '#4CAF50' : '#F44336', marginTop: 4 }}>
                {formatAmount(selectedNode.amount)}
              </div>
            </div>
            <div>
              <strong>Direction:</strong>
              <div style={{ marginTop: 4 }}>
                {selectedNode.direction === 'in' ? '📥 Incoming' : '📤 Outgoing'}
              </div>
            </div>
            <div>
              <strong>Risk Level:</strong>
              <div style={{ 
                color: getRiskColor(selectedNode.riskLevel), 
                fontWeight: 600, 
                marginTop: 4 
              }}>
                {selectedNode.riskLevel}
              </div>
            </div>
            {selectedNode.isExchange && (
              <div>
                <strong>Type:</strong>
                <div style={{ color: '#2196F3', marginTop: 4 }}>🏦 Exchange</div>
              </div>
            )}
            {selectedNode.isSanctioned && (
              <div>
                <strong>Status:</strong>
                <div style={{ color: '#F44336', marginTop: 4 }}>🚨 SANCTIONED</div>
              </div>
            )}
            <div>
              <strong>Step:</strong>
              <div style={{ marginTop: 4 }}>{selectedNode.step}</div>
            </div>
            <div>
              <strong>Timestamp:</strong>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                {new Date(selectedNode.timestamp).toLocaleString()}
              </div>
            </div>
            {selectedNode.transactionHash && (
              <div>
                <strong>Transaction Hash:</strong>
                <div style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>
                  {selectedNode.transactionHash}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div style={{
        position: 'absolute',
        top: 10,
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'white',
        padding: 8,
        borderRadius: 6,
        border: `1px solid ${branding.borderColor}`,
        fontSize: 12,
        zIndex: 10
      }}>
        <strong>Total Nodes: {nodes.length}</strong> | 
        <span style={{ color: '#4CAF50' }}> In: {connections.filter(c => c.direction === 'in').length}</span> | 
        <span style={{ color: '#F44336' }}> Out: {connections.filter(c => c.direction === 'out').length}</span> |
        <span style={{ color: '#2196F3' }}> Connections: {connections.length}</span>
      </div>

      {/* Debug Panel */}
      <details style={{
        position: 'absolute',
        bottom: 10,
        left: 10,
        backgroundColor: 'white',
        border: `1px solid ${branding.borderColor}`,
        borderRadius: 6,
        padding: 8,
        fontSize: 10,
        zIndex: 10,
        maxWidth: 300,
        maxHeight: 200,
        overflow: 'auto'
      }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Debug Info</summary>
        <div style={{ marginTop: 8 }}>
          <div><strong>Trace Steps:</strong> {traceData?.traceSteps?.length || 0}</div>
          <div><strong>Processed Nodes:</strong> {nodes.length}</div>
          <div><strong>Processed Connections:</strong> {connections.length}</div>
          <div><strong>Root Address:</strong> {traceData?.rootAddress}</div>
          {connections.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <strong>Sample Connection:</strong>
              <pre style={{ fontSize: 8, margin: 4, backgroundColor: '#f0f0f0', padding: 4 }}>
                {JSON.stringify(connections[0], null, 2)}
              </pre>
            </div>
          )}
        </div>
      </details>
    </div>
  );
};

export default TransactionFlowDiagram;
